import React, { useState, useEffect, useMemo } from 'react';
import { api, useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Users, Package, Calendar, PoundSterling, Clock, Star } from 'lucide-react';

import { StatCard } from '../components/admin/StatCard';
import { AdminOverviewTab } from '../components/admin/AdminOverviewTab';
import { AdminProvidersTab } from '../components/admin/AdminProvidersTab';
import { AdminUsersTab } from '../components/admin/AdminUsersTab';
import { AdminBookingsTab } from '../components/admin/AdminBookingsTab';
import { AdminPaymentsTab } from '../components/admin/AdminPaymentsTab';
import { AdminPayoutsTab } from '../components/admin/AdminPayoutsTab';
import { AdminReviewsTab } from '../components/admin/AdminReviewsTab';
import { AdminDiscountsTab } from '../components/admin/AdminDiscountsTab';
import { AdminContentTab } from '../components/admin/AdminContentTab';
import { AdminSettingsTab } from '../components/admin/AdminSettingsTab';
import { AdminDeletedTab } from '../components/admin/AdminDeletedTab';
import { AdminMonitoringTab } from '../components/admin/AdminMonitoringTab';
import AdminMessagingTab from '../components/admin/AdminMessagingTab';
import AdminModerationTab from '../components/admin/AdminModerationTab';
import AdminHomepagePhotosTab from '../components/admin/AdminHomepagePhotosTab';
import AdminFeatureRequestsTab from '../components/admin/AdminFeatureRequestsTab';
import { ProviderDetailModal } from '../components/admin/ProviderDetailModal';
import TwoFactorSettingsCard from '../components/account/TwoFactorSettingsCard';
import RecognisedDevicesCard from '../components/account/RecognisedDevicesCard';

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [providers, setProviders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [content, setContent] = useState({ portfolio: [], videos: [], transformations: [] });
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Filters
  const [bookingStatusFilter, setBookingStatusFilter] = useState('all');
  const [bookingProviderFilter, setBookingProviderFilter] = useState('all');
  const [bookingDateFilter, setBookingDateFilter] = useState('');
  const [bookingSourceFilter, setBookingSourceFilter] = useState('all');
  const [providerStatusFilter, setProviderStatusFilter] = useState('all');
  const [contentFilter, setContentFilter] = useState('all');

  // Modal
  const [selectedProvider, setSelectedProvider] = useState(null);

  useEffect(() => {
    // Re-verify session on dashboard mount
    const verifyAndFetch = async () => {
      try {
        await api.get('/auth/me');
        fetchData();
      } catch (err) {
        console.error('Admin session verification failed:', err);
        navigate('/login', { replace: true });
      }
    };
    verifyAndFetch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, providersRes, bookingsRes, usersRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/providers'),
        api.get('/admin/bookings'),
        api.get('/admin/users'),
      ]);
      setStats(statsRes.data);
      setProviders(providersRes.data || []);
      setBookings(bookingsRes.data || []);
      setUsers(usersRes.data || []);
    } catch (err) {
      console.error('Admin data fetch error:', err);
      toast.error('Failed to load admin data');
    } finally { setLoading(false); }
  };

  const fetchTabData = async (tab) => {
    try {
      if (tab === 'reviews' && reviews.length === 0) {
        const res = await api.get('/admin/reviews');
        setReviews(res.data || []);
      }
      if (tab === 'discounts' && discounts.length === 0) {
        const res = await api.get('/admin/discounts');
        setDiscounts(res.data || []);
      }
      if (tab === 'payments' && transactions.length === 0) {
        const res = await api.get('/admin/transactions');
        setTransactions(res.data || []);
      }
      if (tab === 'content' && content.portfolio.length === 0 && content.videos.length === 0) {
        const res = await api.get('/admin/content');
        setContent(res.data || { portfolio: [], videos: [], transformations: [] });
      }
      if (tab === 'settings' && !settings) {
        const res = await api.get('/admin/settings');
        setSettings(res.data);
      }
    } catch (err) { console.error('Failed to load tab data:', err); }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    fetchTabData(tab);
  };

  // === Admin Actions ===
  const handleProviderStatus = async (providerId, status) => {
    try {
      await api.put(`/admin/providers/${providerId}/status?status=${status}`);
      toast.success(`Provider ${status}`);
      setProviders(prev => prev.map(p => p.provider_id === providerId ? { ...p, status } : p));
    } catch { toast.error('Failed to update'); }
  };

  const handleFlagProvider = async (providerId) => {
    try {
      const res = await api.put(`/admin/providers/${providerId}/flag`);
      setProviders(prev => prev.map(p => p.provider_id === providerId ? { ...p, flagged: res.data.flagged } : p));
      toast.success(res.data.message);
    } catch { toast.error('Failed'); }
  };

  const handleDeleteProvider = async (providerId) => {
    if (!window.confirm('Soft-delete this provider? Their profile will be hidden.')) return;
    try {
      await api.delete(`/admin/providers/${providerId}`);
      setProviders(prev => prev.map(p => p.provider_id === providerId ? { ...p, status: 'deleted' } : p));
      toast.success('Provider deleted');
    } catch { toast.error('Failed'); }
  };

  const handleRestoreProvider = async (providerId) => {
    try {
      await api.post(`/admin/providers/${providerId}/restore`);
      setProviders(prev => prev.map(p => p.provider_id === providerId ? { ...p, status: 'pending' } : p));
      toast.success('Provider restored, moved to pending review');
    } catch { toast.error('Failed to restore'); }
  };

  const handlePermanentDeleteProvider = async (providerId) => {
    if (!window.confirm('PERMANENTLY delete this provider and ALL their data? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/providers/${providerId}/permanent`);
      setProviders(prev => prev.filter(p => p.provider_id !== providerId));
      toast.success('Provider permanently deleted');
    } catch { toast.error('Failed to delete permanently'); }
  };

  const handleToggleSuspend = async (user) => {
    const suspending = !user.suspended;
    if (suspending && !window.confirm(`Suspend ${user.name}? They will be signed out, unable to log in, and any listings will be hidden until you lift it.`)) return;
    try {
      const res = await api.post(`/admin/users/${user.user_id}/${suspending ? 'suspend' : 'unsuspend'}`, {});
      setUsers(prev => prev.map(u => u.user_id === user.user_id ? { ...u, suspended: res.data.suspended } : u));
      toast.success(suspending ? 'Account suspended' : 'Suspension lifted');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Remove this review?')) return;
    try {
      await api.delete(`/admin/reviews/${reviewId}`);
      setReviews(prev => prev.filter(r => r.review_id !== reviewId));
      toast.success('Review removed');
    } catch { toast.error('Failed'); }
  };

  const handleToggleDiscount = async (discountId) => {
    try {
      const res = await api.put(`/admin/discounts/${discountId}/toggle`);
      setDiscounts(prev => prev.map(d => d.discount_id === discountId ? { ...d, is_active: res.data.is_active } : d));
      toast.success(res.data.message);
    } catch { toast.error('Failed'); }
  };

  const handleDeleteContent = async (type, id) => {
    if (!window.confirm('Remove this content?')) return;
    try {
      await api.delete(`/admin/content/${type}/${id}`);
      if (type === 'image') setContent(prev => ({ ...prev, portfolio: prev.portfolio.filter(p => p.image_id !== id) }));
      if (type === 'video') setContent(prev => ({ ...prev, videos: prev.videos.filter(v => v.video_id !== id) }));
      if (type === 'transformation') setContent(prev => ({ ...prev, transformations: prev.transformations.filter(t => t.transformation_id !== id) }));
      toast.success('Content removed');
    } catch { toast.error('Failed'); }
  };

  const handleSaveSettings = async () => {
    try {
      await api.put('/admin/settings', settings);
      toast.success('Settings saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // === Filtered / derived data ===
  const filteredProviders = useMemo(() => {
    return providers.filter(p => providerStatusFilter === 'all' || p.status === providerStatusFilter);
  }, [providers, providerStatusFilter]);

  const filteredBookings = useMemo(() => {
    return bookings.filter(b => {
      if (bookingStatusFilter !== 'all' && b.status !== bookingStatusFilter) return false;
      if (bookingProviderFilter !== 'all' && b.provider_id !== bookingProviderFilter) return false;
      if (bookingDateFilter && !b.date?.startsWith(bookingDateFilter)) return false;
      return true;
    });
  }, [bookings, bookingStatusFilter, bookingProviderFilter, bookingDateFilter]);

  const allContent = useMemo(() => {
    const all = [
      ...content.portfolio.map(p => ({ ...p, type: 'image', id: p.image_id, url: p.url })),
      ...content.videos.map(v => ({ ...v, type: 'video', id: v.video_id, url: v.video_url })),
      ...content.transformations.map(t => ({ ...t, type: 'transformation', id: t.transformation_id, url: t.after_image })),
    ];
    if (contentFilter === 'all') return all;
    return all.filter(c => c.type === contentFilter);
  }, [content, contentFilter]);

  const pendingProviders = providers.filter(p => p.status === 'pending');
  const deletedProviders = providers.filter(p => p.status === 'deleted');
  const uniqueProviderNames = [...new Map(providers.map(p => [p.provider_id, p])).values()];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-dashboard">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-medium text-[#1A1A1A]">Admin Dashboard</h1>
          <p className="text-stone-500">Manage Zuriya marketplace</p>
        </div>

        {/* Quick Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-8">
          <StatCard icon={Users} label="Users" value={stats?.total_users || 0} testId="total-users" />
          <StatCard icon={Package} label="Providers" value={stats?.total_providers || 0} />
          <StatCard icon={Clock} label="Pending" value={stats?.pending_providers || 0} color="text-yellow-500" testId="pending-providers" />
          <StatCard icon={Calendar} label="Bookings" value={stats?.total_bookings || 0} color="text-blue-500" />
          <StatCard icon={PoundSterling} label="Revenue" value={`£${(stats?.total_revenue || 0).toFixed(0)}`} color="text-green-500" testId="total-revenue" />
          <StatCard icon={PoundSterling} label="Commission" value={`£${(stats?.total_commission || 0).toFixed(2)}`} color="text-green-600" />
          <StatCard icon={Users} label="New (7d)" value={stats?.new_users_7d || 0} color="text-purple-500" testId="new-users-7d" />
          <StatCard icon={Star} label="Reviews" value={stats?.total_reviews || 0} color="text-amber-500" />
        </div>

        {/* Pending Approvals Alert */}
        {pendingProviders.length > 0 && (
          <div className="card-surface p-4 mb-8 border-l-4 border-l-yellow-500 bg-yellow-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-yellow-600" />
                <span className="font-medium text-yellow-800">
                  {pendingProviders.length} provider{pendingProviders.length > 1 ? 's' : ''} awaiting approval
                </span>
              </div>
              <Button variant="outline" size="sm" onClick={() => { setActiveTab('providers'); setProviderStatusFilter('pending'); }} className="border-yellow-600 text-yellow-700 hover:bg-yellow-100">
                Review Now
              </Button>
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="mb-8 bg-white border border-[#E6E4DD] flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white">Overview</TabsTrigger>
            <TabsTrigger value="providers" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="admin-providers-tab">Providers</TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="admin-users-tab">Users</TabsTrigger>
            <TabsTrigger value="bookings" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="admin-bookings-tab">Bookings</TabsTrigger>
            <TabsTrigger value="payments" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="admin-payments-tab">Payments</TabsTrigger>
            <TabsTrigger value="payouts" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="admin-payouts-tab">Payouts</TabsTrigger>
            <TabsTrigger value="reviews" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="admin-reviews-tab">Reviews</TabsTrigger>
            <TabsTrigger value="discounts" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="admin-discounts-tab">Discounts</TabsTrigger>
            <TabsTrigger value="content" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="admin-content-tab">Content</TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="admin-settings-tab">Settings</TabsTrigger>
            <TabsTrigger value="deleted" className="data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="admin-deleted-tab">
              Deleted{deletedProviders.length > 0 && ` (${deletedProviders.length})`}
            </TabsTrigger>
            <TabsTrigger value="monitoring" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="admin-monitoring-tab">
              Monitoring
            </TabsTrigger>
            <TabsTrigger value="messaging" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="admin-messaging-tab-trigger">
              Messaging
            </TabsTrigger>
            <TabsTrigger value="moderation" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="admin-moderation-tab-trigger">
              Moderation
            </TabsTrigger>
            <TabsTrigger value="homepage-photos" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="admin-homepage-photos-tab-trigger">
              Homepage
            </TabsTrigger>
            <TabsTrigger value="feature-requests" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="admin-feature-requests-tab-trigger">
              Feedback
            </TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="admin-security-tab">
              Security
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <AdminOverviewTab stats={stats} bookings={bookings} />
          </TabsContent>

          <TabsContent value="providers" data-testid="providers-tab">
            <AdminProvidersTab
              filteredProviders={filteredProviders}
              providerStatusFilter={providerStatusFilter}
              setProviderStatusFilter={setProviderStatusFilter}
              onStatusChange={handleProviderStatus}
              onFlag={handleFlagProvider}
              onDelete={handleDeleteProvider}
              onViewProvider={setSelectedProvider}
            />
          </TabsContent>

          <TabsContent value="users" data-testid="users-tab">
            <AdminUsersTab users={users} onToggleSuspend={handleToggleSuspend} />
          </TabsContent>

          <TabsContent value="bookings" data-testid="bookings-tab">
            <AdminBookingsTab
              filteredBookings={filteredBookings}
              bookingStatusFilter={bookingStatusFilter}
              setBookingStatusFilter={setBookingStatusFilter}
              bookingProviderFilter={bookingProviderFilter}
              setBookingProviderFilter={setBookingProviderFilter}
              bookingDateFilter={bookingDateFilter}
              setBookingDateFilter={setBookingDateFilter}
              bookingSourceFilter={bookingSourceFilter}
              setBookingSourceFilter={setBookingSourceFilter}
              uniqueProviderNames={uniqueProviderNames}
            />
          </TabsContent>

          <TabsContent value="payments" data-testid="payments-tab">
            <AdminPaymentsTab stats={stats} transactions={transactions} />
          </TabsContent>

          <TabsContent value="payouts" data-testid="payouts-tab">
            <AdminPayoutsTab />
          </TabsContent>

          <TabsContent value="reviews" data-testid="reviews-tab">
            <AdminReviewsTab reviews={reviews} onDeleteReview={handleDeleteReview} />
          </TabsContent>

          <TabsContent value="discounts" data-testid="discounts-tab">
            <AdminDiscountsTab discounts={discounts} onToggleDiscount={handleToggleDiscount} />
          </TabsContent>

          <TabsContent value="content" data-testid="content-tab">
            <AdminContentTab
              allContent={allContent}
              contentFilter={contentFilter}
              setContentFilter={setContentFilter}
              onDeleteContent={handleDeleteContent}
              onRefresh={fetchData}
            />
          </TabsContent>

          <TabsContent value="settings" data-testid="settings-tab">
            <AdminSettingsTab settings={settings} setSettings={setSettings} onSaveSettings={handleSaveSettings} />
          </TabsContent>

          <TabsContent value="deleted" data-testid="deleted-tab">
            <AdminDeletedTab
              deletedProviders={deletedProviders}
              onRestore={handleRestoreProvider}
              onPermanentDelete={handlePermanentDeleteProvider}
              onViewProvider={setSelectedProvider}
            />
          </TabsContent>

          <TabsContent value="monitoring" data-testid="monitoring-tab">
            <AdminMonitoringTab />
          </TabsContent>

          <TabsContent value="messaging" data-testid="messaging-tab">
            <AdminMessagingTab />
          </TabsContent>

          <TabsContent value="moderation" data-testid="moderation-tab">
            <AdminModerationTab />
          </TabsContent>

          <TabsContent value="homepage-photos" data-testid="homepage-photos-tab">
            <AdminHomepagePhotosTab />
          </TabsContent>

          <TabsContent value="feature-requests" data-testid="feature-requests-tab">
            <AdminFeatureRequestsTab />
          </TabsContent>

          <TabsContent value="security" data-testid="security-tab">
            <div className="space-y-6">
              <TwoFactorSettingsCard />
              <RecognisedDevicesCard />
            </div>
          </TabsContent>
        </Tabs>

        <ProviderDetailModal
          provider={selectedProvider}
          onClose={() => setSelectedProvider(null)}
          onProviderUpdate={(updated) => {
            setProviders(prev => prev.map(p =>
              p.provider_id === updated.provider_id ? { ...p, ...updated } : p
            ));
          }}
        />
      </div>
    </div>
  );
}
